import { Img } from 'openimg/react'
import { useRef } from 'react'
import { Link, Form } from 'react-router'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { useUser, userHasRole } from '#app/utils/user.ts'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuPortal,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from './ui/dropdown-menu'
import { Icon } from './ui/icon'

export function UserDropdown() {
	const user = useUser()
	const formRef = useRef<HTMLFormElement>(null)
	const isAdmin = userHasRole(user, 'admin') || userHasRole(user, 'superadmin')
	
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary">
					<Link
						to={`/users/${user.username}`}
						// this is for progressive enhancement
						onClick={(e) => e.preventDefault()}
						className="flex items-center gap-2"
					>
						<Img
							className="h-8 w-8 rounded-full object-cover"
							alt={user.name ?? user.username}
							src={getUserImgSrc(user.image?.objectKey)}
							width={256}
							height={256}
						/>
						<div className="flex flex-col items-start">
							<span className="text-body-sm font-bold">
								{user.name ?? user.username}
							</span>
							{user.isImpersonating && (
								<Badge variant="secondary" className="text-xs">
									Role-playing as {user.impersonatedRoleName}
								</Badge>
							)}
						</div>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="end">
					{user.isImpersonating && (
						<>
							<DropdownMenuItem asChild>
								<Form action="/action/role-play" method="POST">
									<input type="hidden" name="intent" value="stop-role-play" />
									<button type="submit" className="w-full flex items-center">
										<Icon className="text-body-md mr-2" name="exit">
											Stop Role-Playing
										</Icon>
									</button>
								</Form>
							</DropdownMenuItem>
							<DropdownMenuSeparator />
						</>
					)}
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}`}>
							<Icon className="text-body-md" name="avatar">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					{isAdmin && !user.isImpersonating && (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem asChild>
								<Link prefetch="intent" to="/admin/role-play">
									<Icon className="text-body-md" name="dots-horizontal">
										Role Play As...
									</Icon>
								</Link>
							</DropdownMenuItem>
						</>
					)}
					<DropdownMenuSeparator />
					<Form action="/logout" method="POST" ref={formRef}>
						<DropdownMenuItem asChild>
							<button type="submit" className="w-full">
								<Icon className="text-body-md" name="exit">
									Logout
								</Icon>
							</button>
						</DropdownMenuItem>
					</Form>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
